import React, { useState } from 'react';
import { Modal, Form, Input, message } from 'antd';
import { resetUserPassword } from '../../api/user';
import User from '../../types/User';
import { useTexts } from '../../hooks/useTexts';

interface Props {
    open: boolean;
    onClose: () => void;
    user: User | null;
}

const ResetPasswordModal: React.FC<Props> = ({ open, onClose, user }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const { t } = useTexts();

    const newPassword = Form.useWatch('newPassword', form);
    const confirmPassword = Form.useWatch('confirmPassword', form);
    const isOkDisabled = !newPassword || !confirmPassword;

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            if (values.newPassword !== values.confirmPassword) {
                message.error('Şifrələr uyğun gəlmir!');
                return;
            }
            if (!user) return;
            
            setLoading(true);
            await resetUserPassword(user.id, {
                newPassword: values.newPassword,
                confirmPassword: values.confirmPassword
            });
            message.success('Şifrə uğurla yeniləndi!');
            form.resetFields();
            onClose();
        } catch (error: any) {
            console.error('Failed to reset password:', error);
            message.error(error?.response?.data?.message || 'Şifrə yenilənərkən xəta baş verdi');
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        form.resetFields();
        onClose();
    };

    // Reset form when modal opens
    React.useEffect(() => {
        if (open) {
            form.resetFields();
        }
    }, [open, form]);

    return (
        <Modal
            title={`Şifrəni yenilə - ${user?.username || ''}`}
            open={open}
            onOk={handleOk}
            onCancel={handleCancel}
            confirmLoading={loading}
            okButtonProps={{ disabled: isOkDisabled }}
            okText="Yadda saxla"
            cancelText="Ləğv et"
        >
            <Form form={form} layout="vertical">
                <Form.Item
                    name="newPassword"
                    label="Yeni Şifrə"
                    rules={[{ required: true, message: 'Zəhmət olmasa yeni şifrəni daxil edin!' }]}
                >
                    <Input.Password placeholder="Yeni şifrə" />
                </Form.Item>
                <Form.Item
                    name="confirmPassword"
                    label="Yeni Şifrə (təkrar)"
                    dependencies={['newPassword']}
                    rules={[
                        { required: true, message: 'Zəhmət olmasa yeni şifrəni təkrar daxil edin!' },
                        ({ getFieldValue }) => ({
                            validator(_, value) {
                                if (!value || getFieldValue('newPassword') === value) {
                                    return Promise.resolve();
                                }
                                return Promise.reject(new Error('Şifrələr uyğun gəlmir!'));
                            },
                        }),
                    ]}
                >
                    <Input.Password placeholder="Yeni şifrəni təsdiqləyin" />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default ResetPasswordModal;
